import { createContext, useContext } from 'react'
import type { IslandExitSide } from '#src/components/rich-text-editor/islandTraversal.ts'

/**
 * The actions an open island's toolbar (design §2.b) invokes on the host. The
 * island is a LOCAL editor with no access to the host editor, the accept
 * pipeline, or persistence; it reaches them through this context, which
 * `CollaborativeEditor` provides and the host-owned island consumes — the same
 * bridge pattern as `AIProposalActionsContext` / `SuggestionRevealContext`. Null
 * on bare/standalone editors, where the island falls back to a plain close.
 */
export interface IslandActions {
  /**
   * Accept the suggestion using the island's CURRENT content, run through the
   * full accept pipeline (revalidated). The content is passed LIVE because the
   * persisted `editedContent` write is async (patch + refetch) and not
   * synchronously readable back at accept time.
   */
  acceptWithEdits: (id: string, editedHtml: string) => void | Promise<void>
  /** Reject the suggestion (removes the decoration + persists 'rejected'). */
  reject: (id: string) => void
  /**
   * Persist the island's current content as `editedContent` — debounced while
   * open and once on close — so a touched suggestion survives an AI rerun and a
   * later plain Accept uses the edit.
   */
  persistEdited: (id: string, editedHtml: string) => void
  /** Clear the persisted `editedContent` (Restore original). */
  clearEdited: (id: string) => void
  /**
   * Run the host note's undo/redo chain — the exact command chain a
   * host-focused Cmd+Z runs (a suggestion-reject re-pend first, then the
   * document UndoManager), plus the per-keystroke scroll and autosave work the
   * host's own keydown handler cannot do for a keystroke made inside a widget.
   * Focuses the host when something was actually undone, so the island can hand
   * the surface over. Returns whether anything ran.
   */
  hostUndo: () => boolean
  hostRedo: () => boolean
  /**
   * Put the host caret on `side` of this suggestion's card and focus the host —
   * the arrow-key exit out of an island. False when that side has no host
   * position distinct from the card's own anchor, which leaves the caret in the
   * island rather than moving it somewhere that walks straight back in.
   */
  exitToHost: (id: string, side: IslandExitSide) => boolean
}

const IslandActionsContext = createContext<IslandActions | null>(null)

export const IslandActionsProvider = IslandActionsContext.Provider

export function useIslandActions(): IslandActions | null {
  return useContext(IslandActionsContext)
}
